import { AlgoliaApi, AlgoliaFilter, AlgoliaIndex, BrowseConfig } from '@model/iceberg/service/algolia/AlgoliaApi';
import { AlgoliaState } from '@model/state/algolia-state';
import { AirportResult, GeographyResult, HelpArticleResult, ToursSearchParams } from '@model/search';
import { TripTypes } from '@model/common/tours/trip-types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAlgoliaCacheAirports, getAlgoliaCacheToursByType } from '@state/slices/algolia/algoliaSelectors';
import { getToursStateNameByType } from '@util/tours';
import { compact, flatten, map, uniqBy } from 'lodash';
import { getNavDestinations } from '@state/cms';
import { GlobalAppState } from '@model/state';

export enum AlgoliaActions {
  NAME = '@ALGOLIA',
  SET_GEOGRAPHY_QUERY = '@ALGOLIA/setGeographyQuery',
  SET_GEOGRAPHY = '@ALGOLIA/setGeography',
  SEARCH_GEOGRAPHY = '@ALGOLIA/searchGeography',
  SET_AIRPORTS_QUERY = '@ALGOLIA/setAirportsQuery',
  SET_AIRPORTS = '@ALGOLIA/setAirports',
  SEARCH_AIRPORTS = '@ALGOLIA/searchAirports',
  FETCH_DESTINATIONS = '@ALGOLIA/FETCH_DESTINATIONS',
  SET_HELP_ARTICLES = '@ALGOLIA/setHelpArticles',
  SET_HELP_ARTICLES_QUERY = '@ALGOLIA/setHelpArticlesQuery',
  SEARCH_HELP_ARTICLES = '@ALGOLIA/searchHelpArticles',
  FETCH_TOURS_BY_TYPE = '@ALGOLIA/fetchToursByType',
  SET_TOURS_QUERY_BY_TYPE = '@ALGOLIA/setToursQueryByType',
  SET_TOURS_BY_TYPE = '@ALGOLIA/setToursByType',
  SEARCH_TOURS_BY_TYPE = '@ALGOLIA/searchToursByType'
}

export const INITIAL_ALGOLIA_STATE: AlgoliaState = {
  geography: {
    data: [],
    query: '',
    error: {},
    loading: false
  },
  airports: {
    data: [],
    query: '',
    error: {},
    loading: false
  },
  helpArticles: {
    data: [],
    query: '',
    error: {},
    loading: false
  },
  escortedTours: {
    data: [],
    airports: [],
    query: '',
    error: {},
    loading: false
  },
  privateTours: {
    data: [],
    airports: [],
    query: '',
    error: {},
    loading: false
  },
  riverCruises: {
    data: [],
    airports: [],
    query: '',
    error: {},
    loading: false
  },
  multiCentres: {
    data: [],
    airports: [],
    query: '',
    error: {},
    loading: false
  },
  cruises: {
    data: [],
    airports: [],
    query: '',
    error: {},
    loading: false
  },
  cache: {
    airports: null,
    destinations: null,
    escortedTours: null,
    privateTours: null,
    riverCruises: null,
    multiCentres: null,
    cruises: null
  }
};

export const getAlgoliaIndexByType = (tripType: TripTypes) => {
  switch (tripType) {
    case TripTypes.PRIVATE_TOURS:
      return AlgoliaIndex.PRIVATE_TOURS;
    case TripTypes.RIVER_CRUISES:
      return AlgoliaIndex.RIVER_CRUISES;
    case TripTypes.MULTI_CENTRE:
      return AlgoliaIndex.MULTI_CENTRE;
    case TripTypes.CRUISES:
      return AlgoliaIndex.CRUISES;
    default:
      return AlgoliaIndex.TOURS;
  }
};

export const fetchDestinations = createAsyncThunk(
  AlgoliaActions.FETCH_DESTINATIONS,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { response, error } = await performBrowseGeography({
        index: AlgoliaIndex.GEOGRAPHY,
        filters: AlgoliaFilter.DESTINATIONS
      });

      if (error || !response) {
        return rejectWithValue(error);
      }

      if (response) {
        dispatch(
          setGeography(
            response.sort((a: GeographyResult, b: GeographyResult) => a.name.display.localeCompare(b.name.display))
          )
        );
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchToursByType = createAsyncThunk(
  AlgoliaActions.FETCH_TOURS_BY_TYPE,
  async (tripType: TripTypes, { getState, rejectWithValue }) => {
    const state: GlobalAppState = getState() as GlobalAppState;

    const cacheTours = getAlgoliaCacheToursByType(tripType)(state);
    if (cacheTours) {
      return { tripType, tours: cacheTours };
    }

    try {
      const algoliaIndex = getAlgoliaIndexByType(tripType);
      const { response, error } = await performBrowseGeography({
        index: algoliaIndex,
        filters: AlgoliaFilter.TOUR_DESTINATIONS
      });

      if (error || !response) {
        return rejectWithValue(tripType);
      }

      const navDestinations = getNavDestinations(state);
      const allNavDestinations = navDestinations
        ? Object.values(navDestinations).flatMap((t: any) => t?.destinations || [])
        : [];
      const tours = response.map((tour: any) => {
        const { path, isoCode } = tour;
        if (!/^\w{2}$/.test(isoCode)) {
          const match = allNavDestinations.find((destination: any) => destination.path === path);
          if (match?.isoCode) {
            return {
              ...tour,
              isoCode: match.isoCode
            };
          }
        }
        return tour;
      });
      return { tripType, tours };
    } catch (error) {
      return rejectWithValue({ tripType, error });
    }
  }
);

export const searchGeography = createAsyncThunk(
  AlgoliaActions.SEARCH_GEOGRAPHY,
  async (query: string, { dispatch, getState }) => {
    const state = getState() as GlobalAppState;
    if (query) {
      const api = new AlgoliaApi();
      const response = await api.search<GeographyResult>({
        index: AlgoliaIndex.GEOGRAPHY,
        query
      });
      if (response) {
        dispatch(setGeography(response.hits));
      }
    } else {
      const cacheDestinations = state.algolia.cache.destinations;
      if (cacheDestinations) {
        dispatch(setGeography(cacheDestinations));
      } else {
        dispatch(fetchDestinations());
      }
    }
  }
);

export interface AirportsSearchParams {
  query: string;
}

export const searchAirports = createAsyncThunk(
  AlgoliaActions.SEARCH_AIRPORTS,
  async (
    {
      query
    }: {
      query: string;
    },
    { dispatch, getState }
  ) => {
    const state = getState() as GlobalAppState;
    const cacheAirports = getAlgoliaCacheAirports(state);

    if (!query && cacheAirports?.length) {
      dispatch(setAirports({ query: cacheAirports }));
    } else {
      const api = new AlgoliaApi();
      const response = await api.search<AirportResult>({
        index: AlgoliaIndex.AIRPORT,
        query
      });
      if (response) {
        dispatch(setAirports({ query: response.hits }));
      }
    }
  }
);

export const searchHelpArticles = createAsyncThunk(
  AlgoliaActions.SEARCH_HELP_ARTICLES,
  async (query: string, { dispatch }) => {
    if (query) {
      const api = new AlgoliaApi();
      const response = await api.search<HelpArticleResult>({
        index: AlgoliaIndex.HELP_CENTRE,
        query
      });
      if (response) {
        dispatch(setHelpArticles(response.hits));
      }
    }
  }
);

export const searchToursByType = createAsyncThunk(
  AlgoliaActions.SEARCH_TOURS_BY_TYPE,
  async ({ query, tripType }: ToursSearchParams, { dispatch }) => {
    if (query) {
      const algoliaIndex = getAlgoliaIndexByType(tripType);
      const api = new AlgoliaApi();
      const response = await api.search<GeographyResult>({
        index: algoliaIndex,
        query
      });
      if (response) {
        dispatch(setToursByType({ tripType, tours: response.hits }));
      }
    } else {
      dispatch(fetchToursByType(tripType));
    }
  }
);

export async function performSearchTours(payload: ToursSearchParams) {
  const { tripType, query } = payload;
  const algoliaIndex = getAlgoliaIndexByType(tripType);
  const config = {
    index: algoliaIndex,
    query
  };

  const api: AlgoliaApi = new AlgoliaApi();
  try {
    const response = await api.search<GeographyResult>(config);
    return {
      response
    };
  } catch (error) {
    return {
      error
    };
  }
}

export async function performBrowseGeography(config: BrowseConfig) {
  try {
    const api = new AlgoliaApi();
    const response = await api.browse(config);
    return { response };
  } catch (error) {
    return { error };
  }
}

/* ***************** *
 *     REDUCER       *
 * ***************** */
const algoliaSlice = createSlice({
  name: AlgoliaActions.NAME,
  initialState: INITIAL_ALGOLIA_STATE,
  reducers: {
    setGeography: (state, action: PayloadAction<Array<GeographyResult>>) => {
      const payload = action.payload;
      const cacheDestinations = state.cache.destinations || payload;

      return {
        ...state,
        geography: { ...state.geography, data: payload, loading: false },
        cache: { ...state.cache, destinations: cacheDestinations }
      };
    },
    setGeographyQuery: (state, action: PayloadAction<string>) => {
      state.geography.query = action.payload;
      state.geography.loading = !!action.payload || !state.geography.data.length;
    },
    setAirports: (
      state: AlgoliaState,
      action: PayloadAction<{
        query: Array<AirportResult>;
      }>
    ) => {
      const { query } = action.payload;
      const cacheAirports = state.cache.airports || query;

      state.airports = {
        ...state.airports,
        data: query,
        loading: false
      };
      state.cache = { ...state.cache, airports: cacheAirports };
    },
    setAirportsQuery: (
      state,
      action: PayloadAction<{
        query: string;
        tripType?: TripTypes;
      }>
    ) => {
      const { query } = action.payload;
      state.airports.query = query;
      state.airports.loading = !!query;
    },
    setHelpArticles: (state, action: PayloadAction<Array<HelpArticleResult>>) => {
      state.helpArticles.data = action.payload;
      state.helpArticles.loading = !!action.payload;
    },
    setHelpArticlesQuery: (state, action: PayloadAction<string>) => {
      state.helpArticles.query = action.payload;
      state.helpArticles.loading = true;
    },
    setToursByType: (state, action: PayloadAction<any>) => {
      const { tripType, tours } = action.payload;
      const stateName = getToursStateNameByType(tripType);
      state[stateName] = { ...state[stateName], data: tours, loading: false };
    },
    setToursQueryByType: (state, action: PayloadAction<ToursSearchParams>) => {
      const { tripType, query } = action.payload;
      const stateName = getToursStateNameByType(tripType);
      state[stateName].query = query;
      state[stateName].loading = !!query || !state[stateName].data.length;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDestinations.pending, (state) => {
        state.geography.loading = true;
      })
      .addCase(fetchDestinations.rejected, (state) => {
        state.geography.loading = false;
        state.geography.data = [];
      })
      .addCase(fetchToursByType.pending, (state, action: any) => {
        const stateName = getToursStateNameByType(action.payload);
        state[stateName].loading = true;
      })
      .addCase(fetchToursByType.fulfilled, (state, action: any) => {
        const { tripType, tours } = action.payload;
        const stateName = getToursStateNameByType(tripType);
        const cacheTours = state.cache[stateName] || tours;

        const validToursAirports = uniqBy(
          flatten(map(tours, 'airports').filter((airports) => Array.isArray(airports))),
          'code'
        );
        const storedAirports = state.airports.data;
        const validAirports = compact(
          validToursAirports.map(({ code }) =>
            storedAirports.find(({ airport: { code: airportCode } }) => code === airportCode)
          )
        );

        state[stateName] = {
          ...state[stateName],
          data: tours,
          airports: validAirports,
          loading: false
        };
        state.cache[stateName] = cacheTours;
      })
      .addCase(fetchToursByType.rejected, (state, action: any) => {
        const stateName = getToursStateNameByType(action.payload);
        return { ...state, [stateName]: { ...state[stateName], data: [], loading: false } };
      });
  }
});

export const {
  setGeography,
  setGeographyQuery,
  setAirports,
  setAirportsQuery,
  setHelpArticles,
  setHelpArticlesQuery,
  setToursByType,
  setToursQueryByType
} = algoliaSlice.actions;

export const algoliaReducer = algoliaSlice.reducer;
